/**
 * Security monitoring for dynamic function execution
 */
export class SecurityMonitor {
   constructor() {
      this.executionLog = []
      this.suspiciousAttempts = []
      this.validationFailures = []
   }

   /**
    * Log a function execution attempt
    * @param {string} productCode - Product code
    * @param {string} functionCode - Function code being executed
    * @param {boolean} success - Whether execution was successful
    * @param {string} error - Error message if failed
    * @param {number} executionTime - Time taken in milliseconds
    */
   logExecution(
      productCode,
      functionCode,
      success,
      error = null,
      executionTime = 0,
   ) {
      const logEntry = {
         timestamp: new Date().toISOString(),
         productCode,
         functionCode: functionCode.substring(0, 200), // Truncate for logging
         success,
         error,
         executionTime,
         hash: this.hashFunction(functionCode),
      }

      this.executionLog.push(logEntry)

      // Log to console for immediate visibility
      if (success) {
         console.log(
            `[SECURITY] Function executed successfully for product ${productCode} (${executionTime}ms)`,
         )
      } else {
         console.error(
            `[SECURITY] Function execution failed for product ${productCode}: ${error}`,
         )
      }

      // Keep only last 1000 entries
      if (this.executionLog.length > 1000) {
         this.executionLog.shift()
      }
   }

   /**
    * Log a suspicious attempt
    * @param {string} productCode - Product code
    * @param {string} functionCode - Suspicious function code
    * @param {string} reason - Reason for suspicion
    * @param {string} layer - Which security layer caught it
    */
   logSuspiciousAttempt(productCode, functionCode, reason, layer = 'unknown') {
      const suspiciousEntry = {
         timestamp: new Date().toISOString(),
         productCode,
         functionCode: functionCode.substring(0, 500),
         reason,
         layer,
         hash: this.hashFunction(functionCode),
      }

      this.suspiciousAttempts.push(suspiciousEntry)

      // Alert immediately
      console.warn(
         `[SECURITY ALERT] ${layer} - Suspicious function detected for product ${productCode}: ${reason}`,
      )

      // Keep only last 100 suspicious attempts
      if (this.suspiciousAttempts.length > 100) {
         this.suspiciousAttempts.shift()
      }
   }

   /**
    * Log validation failure with details
    * @param {string} productCode - Product code
    * @param {string} functionCode - Function code that failed validation
    * @param {string} validationType - Type of validation that failed
    * @param {string} details - Detailed error information
    */
   logValidationFailure(productCode, functionCode, validationType, details) {
      const validationEntry = {
         timestamp: new Date().toISOString(),
         productCode,
         functionCode: functionCode.substring(0, 300),
         validationType,
         details,
         hash: this.hashFunction(functionCode),
      }

      this.validationFailures.push(validationEntry)

      console.warn(
         `[SECURITY] ${validationType} validation failed for product ${productCode}: ${details}`,
      )

      // Keep only last 200 validation failures
      if (this.validationFailures.length > 200) {
         this.validationFailures.shift()
      }
   }

   /**
    * Simple hash function for function code
    * @param {string} code - Code to hash
    * @returns {string} - Hash value
    */
   hashFunction(code) {
      let hash = 0
      for (let i = 0; i < code.length; i++) {
         const char = code.charCodeAt(i)
         hash = (hash << 5) - hash + char
         hash = hash & hash // Convert to 32-bit integer
      }
      return hash.toString(16)
   }

   /**
    * Get execution statistics
    * @returns {Object} - Statistics object
    */
   getStats() {
      const now = Date.now()
      const oneHourAgo = now - 60 * 60 * 1000

      const recentExecutions = this.executionLog.filter(
         (entry) => new Date(entry.timestamp).getTime() > oneHourAgo,
      )

      const recentSuspicious = this.suspiciousAttempts.filter(
         (entry) => new Date(entry.timestamp).getTime() > oneHourAgo,
      )

      const recentValidationFailures = this.validationFailures.filter(
         (entry) => new Date(entry.timestamp).getTime() > oneHourAgo,
      )

      // Calculate average execution time
      const successfulExecutions = this.executionLog.filter(
         (e) => e.success && e.executionTime > 0,
      )
      const avgExecutionTime =
         successfulExecutions.length > 0
            ? (
                 successfulExecutions.reduce(
                    (sum, e) => sum + e.executionTime,
                    0,
                 ) / successfulExecutions.length
              ).toFixed(2)
            : 0

      return {
         totalExecutions: this.executionLog.length,
         recentExecutions: recentExecutions.length,
         totalSuspicious: this.suspiciousAttempts.length,
         recentSuspicious: recentSuspicious.length,
         totalValidationFailures: this.validationFailures.length,
         recentValidationFailures: recentValidationFailures.length,
         successRate:
            this.executionLog.length > 0
               ? (
                    (this.executionLog.filter((e) => e.success).length /
                       this.executionLog.length) *
                    100
                 ).toFixed(2)
               : 0,
         avgExecutionTime: avgExecutionTime,
         securityLayerBreakdown: this.getSecurityLayerBreakdown(),
      }
   }

   /**
    * Get breakdown of which security layers are catching attacks
    * @returns {Object} - Layer breakdown
    */
   getSecurityLayerBreakdown() {
      const breakdown = {}

      this.suspiciousAttempts.forEach((attempt) => {
         const layer = attempt.layer || 'unknown'
         breakdown[layer] = (breakdown[layer] || 0) + 1
      })

      this.validationFailures.forEach((failure) => {
         const layer = failure.validationType || 'unknown'
         breakdown[layer] = (breakdown[layer] || 0) + 1
      })

      return breakdown
   }

   /**
    * Get recent security events for monitoring dashboard
    * @param {number} limit - Number of recent events to return
    * @returns {Array} - Recent security events
    */
   getRecentSecurityEvents(limit = 50) {
      const allEvents = [
         ...this.suspiciousAttempts.map((e) => ({ ...e, type: 'suspicious' })),
         ...this.validationFailures.map((e) => ({
            ...e,
            type: 'validation_failure',
         })),
         ...this.executionLog
            .filter((e) => !e.success)
            .map((e) => ({ ...e, type: 'execution_failure' })),
      ]

      // Sort by timestamp descending
      allEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

      return allEvents.slice(0, limit)
   }
}

// Global instance
export const securityMonitor = new SecurityMonitor()
